import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

export function formatPrice(amount: number, currency: string = "¥"): string {
  // Format based on currency
  switch (currency) {
    case "¥":
    case "₩":
    case "元":
      // No decimal places for these currencies
      return `${currency}${Math.round(amount).toLocaleString()}`;
    case "$":
    case "€":
    case "£":
    case "HK$":
      // Two decimal places for these currencies
      return `${currency}${amount.toFixed(2)}`;
    case "₫":
      // Vietnamese Dong typically shows no decimal places
      return `${Math.round(amount).toLocaleString()}${currency}`;
    case "Rp":
    case "RM":
    case "₱":
    case "฿":
      // These currencies typically show before the amount
      return `${currency} ${Math.round(amount).toLocaleString()}`;
    default:
      // Default format with the currency symbol before the amount
      return `${currency}${Math.round(amount).toLocaleString()}`;
  }
}

// Request deduplication cache
const requestCache = new Map<string, Promise<unknown>>();

// Create a wrapper for deduplicating Supabase requests
export async function dedupedRequest<T>(
  key: string,
  request: Promise<{ data: T | null; error: unknown }>
): Promise<T> {
  const cached = requestCache.get(key);
  if (cached) {
    return cached as Promise<T>;
  }

  const promise = request.then((response) => {
    if (response.error) throw response.error;
    if (!response.data) throw new Error("No data returned");
    return response.data;
  });

  // Set cache with immediate cleanup
  requestCache.set(key, promise);
  promise.finally(() => {
    requestCache.delete(key);
  });

  return promise;
}
